// src/prototype/components/ea_reports/use_case/EAUseCaseArtifactHeader.tsx
// ============================================================
// DVA EA USE CASE ARTIFACT — HEADER COMPONENT
// ============================================================
// Renders the top header band for all use-case print deliverables.
// Navy background, logo, title, client, use case, date, and
// Early Adopter badge.
// ============================================================

import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────

export interface EAUseCaseArtifactHeaderProps {
  /** Primary report title, e.g. "Board Rationale" */
  title: string;
  /** Secondary descriptor, e.g. "Governance Decision Brief" */
  subtitle: string;
  /** Health system / client name */
  clientName: string;
  /** The specific use case this artifact covers */
  useCaseName: string;
  /** Display date string, e.g. "March 2026" */
  reportDate: string;
  /** Optional override for the badge label (default: "Early Adopter Preview") */
  badgeLabel?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export const EAUseCaseArtifactHeader: React.FC<EAUseCaseArtifactHeaderProps> = ({
  title,
  subtitle,
  clientName,
  useCaseName,
  reportDate,
  badgeLabel = 'Early Adopter Preview',
}) => (
  <div className="ua-header">
    {/* ── Left — report identity ──────────────────────── */}
    <div className="ua-header-left">
      <div className="ua-header-badge">{badgeLabel}</div>

      <div className="ua-header-title">{title}</div>
      <div className="ua-header-subtitle">{subtitle}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '6px' }}>
        <div className="ua-header-client">{clientName}</div>
        <div className="ua-header-use-case">{useCaseName}</div>
      </div>
    </div>

    {/* ── Right — logo + date ─────────────────────────── */}
    <div className="ua-header-meta">
      <img
        src="/public/adaptive_product_logo_white.png"
        alt="Adaptive Product"
        className="ua-header-logo"
        onError={(e) => {
          // Fallback text logo if image not found
          (e.target as HTMLImageElement).style.display = 'none';
          const fallback = document.createElement('span');
          fallback.style.cssText = 'font-size:8pt;font-weight:800;color:rgba(255,255,255,0.85);letter-spacing:0.06em;';
          fallback.textContent = 'ADAPTIVE PRODUCT';
          (e.target as HTMLImageElement).parentNode?.appendChild(fallback);
        }}
      />
      <div style={{ textAlign: 'right', marginTop: '6px' }}>
        <div
          style={{
            fontSize: '6.5pt',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '2px',
          }}
        >
          Data Value Accelerator
        </div>
        <div className="ua-header-date">{reportDate}</div>
      </div>
    </div>
  </div>
);

export default EAUseCaseArtifactHeader;